import {
  classifyExtractedHoneyColour,
  type ExtractedHoneyColour,
  type ShowResultsReleasePayloadOutput,
} from '@schemas'
import {
  CATEGORY_ORDER,
  getCategoryChartColor,
  getCategoryLabel,
  resolveCategoryCode,
} from '@/lib/classColors'

// Every Highlights figure is derived here from the released snapshot alone. The public page
// and the staff review share these, so the review shows what the page will.
type Results = ShowResultsReleasePayloadOutput

export const share = (count: number, total: number) => (total > 0 ? (100 * count) / total : null)
// A share that rounds to zero while something was counted reads as "<1%", never as "0%".
export const percent = (value: number | null) =>
  value === null ? 'Unavailable' : value > 0 && value < 0.5 ? '<1%' : `${Math.round(value)}%`
export const counted = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`

export type ClassOdds = {
  modelClassKey: string | null
  key: string
  code: string
  name: string
  division: string
  entries: number
  /** Distinct exhibits with a placing: tied exhibits each count, and none counts twice. */
  placed: number
  /** Percent of entries that placed. Null is unavailable, never zero. */
  odds: number | null
  unavailable: 'No entries' | 'No awards given' | null
}

export function ribbonOdds(results: Results): ClassOdds[] {
  return results.divisions.flatMap(division =>
    division.classes.map(cls => {
      const placed = new Set(cls.placements.map(placement => placement.exhibitId)).size
      const unavailable = cls.entries === 0 ? 'No entries' : cls.noAwards ? 'No awards given' : null
      return {
        modelClassKey: cls.modelClassKey,
        key: `${division.code}-${cls.code}`,
        code: cls.code,
        name: cls.name,
        division: division.name,
        entries: cls.entries,
        placed,
        odds: unavailable ? null : share(placed, cls.entries),
        unavailable,
      }
    })
  )
}

const SHOWN = 4

/**
 * The longest shots and the safest bets, with no class in both. A class with the best odds
 * is never a longest shot, and one with the worst is never a safest bet, so the lists can be
 * shorter than four. With too few classes to fill both, every class is returned as `longest`.
 * With more than that and the same odds in every class there is nothing to rank: `same`
 * holds those odds and both lists are empty.
 */
export function rankOdds(classes: ClassOdds[]) {
  const ranked = classes.filter((cls): cls is ClassOdds & { odds: number } => cls.odds !== null)
  // Larger classes first among equal odds: three of three says more than one of one.
  const tie = (a: ClassOdds, b: ClassOdds) => b.entries - a.entries || a.key.localeCompare(b.key)
  const ascending = [...ranked].sort((a, b) => a.odds - b.odds || tie(a, b))
  if (ranked.length <= SHOWN * 2) return { longest: ascending, safest: [], same: null }
  const worst = ascending[0].odds
  const best = ascending[ascending.length - 1].odds
  if (worst === best) return { longest: [], safest: [], same: worst }
  const longest = ascending.filter(cls => cls.odds < best).slice(0, SHOWN)
  const safest = [...ranked]
    .sort((a, b) => b.odds - a.odds || tie(a, b))
    .filter(cls => cls.odds > worst && !longest.includes(cls))
    .slice(0, SHOWN)
  return { longest, safest, same: null }
}

export type CountRow = { key: string; label: string; entries: number; fill: string }

/** Entries by category, in the app's category order. Null when the show has no entries. */
export function categoryMix(results: Results): CountRow[] | null {
  const rows = new Map<string, CountRow>()
  for (const division of results.divisions)
    for (const cls of division.classes) {
      const code = resolveCategoryCode(cls.categoryCode, division.code)
      const row = rows.get(code) ?? {
        key: code,
        label: getCategoryLabel(code, division.name),
        entries: 0,
        fill: getCategoryChartColor(code),
      }
      row.entries += cls.entries
      rows.set(code, row)
    }
  const position = (code: string) => {
    const index = CATEGORY_ORDER.indexOf(code)
    return index < 0 ? CATEGORY_ORDER.length : index
  }
  const ordered = [...rows.values()].sort((a, b) => position(a.key) - position(b.key))
  return ordered.some(row => row.entries > 0) ? ordered : null
}

const HONEY_COLOURS: { key: ExtractedHoneyColour; label: string; fill: string }[] = [
  { key: 'light', label: 'Light', fill: 'var(--class-chart-honey-light)' },
  { key: 'amber', label: 'Amber', fill: 'var(--class-chart-honey-amber)' },
  { key: 'dark', label: 'Dark', fill: 'var(--class-chart-honey-dark)' },
]

/**
 * Extracted honey entries by colour class, identified from the frozen model key or exact
 * class name. A colour with no identified class is null, and the whole split is null when the
 * show has none of the three.
 */
export function honeyColours(results: Results) {
  const totals = new Map<ExtractedHoneyColour, number>()
  for (const division of results.divisions)
    for (const cls of division.classes) {
      const colour = classifyExtractedHoneyColour(cls)
      if (colour) totals.set(colour, (totals.get(colour) ?? 0) + cls.entries)
    }
  if (!totals.size) return null
  return HONEY_COLOURS.map(colour => ({ ...colour, entries: totals.get(colour.key) ?? null }))
}

/** Null when the snapshot carries no distribution, or nobody entered. */
export function entriesPerEntrant(results: Results) {
  return results.exhibitsPerEntrant.some(bin => bin.entrants > 0)
    ? results.exhibitsPerEntrant
    : null
}
